import React from 'react';
import classNames from 'classnames';
import { kebabCase } from 'lodash/fp';
import { useSelector } from 'react-redux';

// Components
import Icon from '../../../components/icons/Icon';
import Button from '../../../components/buttons/Button';
import FormattingOptionsPopup from './FormattingOptionsPopup';

// Manager
import { useTranslator } from '../../../components/i18n/useTranslator';
import { useActiveTableCallback, useActiveTableSelector } from '../store/hotTableStoreHooks';
import { selectActiveTableCellSelection } from '../store/selectors/activeHotTableSelectors';

// Utils
import { cellTypeUpdateShouldHappen } from '../utils/dataUtils/cell/tableCellTypeUtils';
import { getNormalisedComputedCellValueFromHot } from '../utils/hotInstanceUtils/cell/hotTableCellRenderValueUtils';
import { getAllCellsBetween } from '../utils/selectionUtils/tableSelectionGeometryUtils';
import { translateCellType } from '../../../../common/table/cellTypeTranslations';
import {
    updateHotTableSelectedCellType,
    updateHotTableSelectedCellTypeFormat,
} from '../utils/hotInstanceUtils/cell/hotTableCellUpdateUtils';
import { userCurrencyPreferenceSelector } from '../../../user/currentUserSelector';

// Constants
import { CellTypeConstants, CellTypeNames } from '../../../../common/table/CellTypeConstants';
import { CellTypeObject } from '../../../../common/table/TableTypes';

// Styles
import './CellTypePopup.scss';

type CellTypeButtonProps = {
    currentCellType: CellTypeNames;
    dispatchUpdateCellType?: (elementId: string, cellType: string) => void;
    isActive: boolean;
    allSelectedCells: Array<{ row: number; col: number }>;
    lastClickedType: CellTypeNames | null;
    setLastClickedType: (type: CellTypeNames) => void;
    locale: string;
};

const CellTypeButton: React.FC<CellTypeButtonProps> = (props) => {
    const { currentCellType, isActive, allSelectedCells, lastClickedType, setLastClickedType, locale } = props;
    const { icon, formattingOptions } = CellTypeConstants[currentCellType];

    const translate = useTranslator('popup', 'table');
    const currencyPreference = useSelector(userCurrencyPreferenceSelector);
    const displayName = translateCellType(currentCellType, translate);

    // Does type have formatting options AND
    // do all the selected cells have the current cell type, OR is it the last clicked cell type?
    const showFormattingOptions = !!formattingOptions && (isActive || lastClickedType === currentCellType);

    const isDisabled = useActiveTableSelector((activeTable) => {
        if (!activeTable) return true;

        const activeTableInstance = activeTable.instance;

        return !allSelectedCells.some(({ row, col }) => {
            const normalisedComputedCellValue = getNormalisedComputedCellValueFromHot(
                activeTable.elementId,
                row,
                col,
                activeTableInstance,
            );
            return cellTypeUpdateShouldHappen(normalisedComputedCellValue, currentCellType, locale);
        });
    });

    const updateCellType = useActiveTableCallback(
        (activeTable) => {
            updateHotTableSelectedCellType(
                activeTable.instance,
                activeTable.elementId,
                currentCellType,
                currencyPreference,
            );

            setLastClickedType(currentCellType);
        },
        [currencyPreference, currentCellType],
    );

    const updateCellTypeFormat = useActiveTableCallback(
        (activeTable, typeFormatChanges: CellTypeObject) =>
            updateHotTableSelectedCellTypeFormat(activeTable.instance, typeFormatChanges),
        [],
    );

    return (
        <>
            <Button
                onClick={updateCellType}
                className={classNames('CellTypeButton', kebabCase(currentCellType), {
                    active: isActive,
                    disabled: isDisabled,
                })}
            >
                <Icon name={icon} />
                <span>{displayName}</span>
                {isActive && <Icon name={'block-style-tick'} />}
                {!isActive && !!formattingOptions && <Icon name="chevron-right" />}
            </Button>
            {showFormattingOptions && (
                <FormattingOptionsPopup
                    {...props}
                    showFormattingOptions={showFormattingOptions}
                    updateCellTypeFormat={updateCellTypeFormat}
                />
            )}
        </>
    );
};

type CellTypePopupProps = Omit<CellTypeButtonProps, 'allSelectedCells' | 'currentCellType' | 'isActive'> & {
    activeCellType: CellTypeNames | undefined;
    closePopup: (() => void) | null;
};

const CellTypePopup: React.FC<CellTypePopupProps> = (props) => {
    const { activeCellType } = props;
    const currentCellSelections = useActiveTableSelector(selectActiveTableCellSelection);
    const allSelectedCells = getAllCellsBetween(currentCellSelections);

    return (
        <>
            {Object.values(CellTypeNames).map((currentCellType) => (
                <CellTypeButton
                    {...props}
                    allSelectedCells={allSelectedCells}
                    key={currentCellType}
                    currentCellType={currentCellType}
                    isActive={activeCellType === currentCellType}
                />
            ))}
        </>
    );
};

export default CellTypePopup;
